import type { Location } from 'react-router-dom';
import type { SessionState } from '@/types/test';

/**
 * Predicate-функции маршрутной защиты. Используются ProtectedRoute (через
 * App.tsx) и тестами — поэтому вынесены отдельно от компонента
 * (требование react-refresh: один файл — один компонент).
 *
 * Логика:
 *  - /welcome (`/`) и /child-info — публичные, без ProtectedRoute.
 *  - Остальные — нужен прогресс-инвариант, который мог быть достигнут
 *    только через нормальный flow.
 */

export type RouteGuardPredicate = (state: SessionState, location: Location) => boolean;

export const guardInstruction: RouteGuardPredicate = (state) =>
  state.child !== null && state.ageGroup !== null;

export const guardStage1: RouteGuardPredicate = (state) =>
  state.child !== null && state.ageGroup !== null;

/** На /between-stages пускаем, когда Stage 1 завершён — есть все 11 ответов. */
export const guardBetweenStages: RouteGuardPredicate = (state) =>
  state.child !== null && state.stage1Answers.length >= 11;

/** На /stage2 — те же условия что на between-stages. */
export const guardStage2: RouteGuardPredicate = (state) =>
  state.child !== null && state.stage1Answers.length >= 11;

/** На /contacts пускаем, когда Stage 2 начат (есть хотя бы один ответ). */
export const guardContacts: RouteGuardPredicate = (state) =>
  state.child !== null && state.stage2Answers.length > 0;

/** На /loading — после submit контактов (есть phone). */
export const guardLoading: RouteGuardPredicate = (state) => state.contacts !== null;

/** На /result — есть child и оба этапа отвечены. */
export const guardResult: RouteGuardPredicate = (state) =>
  state.child !== null &&
  state.stage1Answers.length > 0 &&
  state.stage2Answers.length > 0;

/**
 * /confirmation — лид submitted (форма ContactsForm заполнена). Result
 * CTA «Записаться на бесплатное пробное» navigate'ит сюда напрямую.
 * contacts устанавливается на ContactsForm submit (Экран 7) — задолго
 * ДО Result/Confirmation, race condition при propagation state физически
 * исключён.
 */
export const guardConfirmation: RouteGuardPredicate = (state) =>
  state.contacts !== null;

/**
 * /error — особый случай. Допускаем заход ТОЛЬКО если в router-state есть
 * errorType (значит, нас сюда увёл код через navigate('/error', { state })).
 * Прямой URL / reload — router-state теряется → редирект на `/`.
 */
interface ErrorRouterState {
  errorType?: string;
}
export const guardError: RouteGuardPredicate = (_state, location) => {
  const navState = (location.state as ErrorRouterState | null) ?? null;
  return Boolean(navState?.errorType);
};
